// SectionCard.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Clock,
  Pencil,
  Trash2,
  ListChecks,
  Settings2,
  Headphones,
  Layout,
  Sparkles, // ✅ اضافه
  MessageSquareText, // ✅ اضافه
  Mic, // ✅ اضافه
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../common/ConfirmModal";

function pickTitle(section) {
  if (section?.title_fa || section?.title_en)
    return section.title_fa || section.title_en || "-";
  if (typeof section?.title === "string") return section.title || "-";
  if (section?.title?.fa || section?.title?.en)
    return section.title.fa || section.title.en || "-";
  return "-";
}

function pickDescriptionHtml(section) {
  if (section?.description_fa || section?.description_en) {
    return section.description_fa || section.description_en || "";
  }
  if (typeof section?.description === "string") return section.description || "";
  if (section?.description?.fa || section?.description?.en) {
    return section.description.fa || section.description.en || "";
  }
  return "";
}

export default function SectionCard({ section, index, onEdit, onDelete, onManageItems }) {
  const { t } = useTranslation();

  const isAiSection = section?.type === "ai";
  const aiMode = section?.ai?.mode === "voice" ? "voice" : section?.ai?.mode === "text" ? "text" : null;

  const itemsCount = Array.isArray(section?.items) ? section.items.length : 0;
  const aiCount = typeof section?.ai?.count === "number" ? section.ai.count : null;

  const time = section?.timeLimitSec ? Math.ceil(section.timeLimitSec / 60) : null;

  const titleText = pickTitle(section);
  const descHtml = pickDescriptionHtml(section);

  const isListeningSection = !!section?.audioFile?.url || section?.kind === "listening";

  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleConfirmDelete = () => {
    onDelete?.(section, index);
    setDeleteOpen(false);
  };

  return (
    <>
      <div className="bg-white rounded-xl border shadow-sm p-4 flex flex-col gap-2">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900">
              {t("exam_builder.section_title_index", { index: (index ?? 0) + 1 })}
              {" — "}
              {titleText}
            </h3>

            {/* ✅ Badge نوع سکشن */}
            {isAiSection && (
              <div className="mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                <Sparkles className="w-3.5 h-3.5" />
                سکشن هوش مصنوعی
                {aiMode === "text" && (
                  <span className="inline-flex items-center gap-1 ms-2 text-indigo-700">
                    <MessageSquareText className="w-3.5 h-3.5" /> متنی
                  </span>
                )}
                {aiMode === "voice" && (
                  <span className="inline-flex items-center gap-1 ms-2 text-indigo-700">
                    <Mic className="w-3.5 h-3.5" /> گویشی
                  </span>
                )}
              </div>
            )}

            <div
              className="text-sm text-gray-600 mt-1 line-clamp-2"
              dangerouslySetInnerHTML={{ __html: descHtml }}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onEdit?.(section, index)}
              className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-indigo-600"
              title={t("exam_builder.edit_section")}
              aria-label={t("exam_builder.edit_section")}
            >
              <Pencil size={18} />
            </button>

            <button
              onClick={() => setDeleteOpen(true)}
              className="p-2 rounded-full text-red-500 hover:bg-red-100 hover:text-red-700"
              title={t("exam_builder.delete_section")}
              aria-label={t("exam_builder.delete_section")}
            >
              <Trash2 size={18} />
            </button>

            {/* ✅ Manage Items برای AI غیرفعال */}
            <button
              onClick={() => {
                if (!isAiSection) onManageItems?.(section, index);
              }}
              disabled={isAiSection}
              className={`p-2 rounded-full ${
                isAiSection
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-500 hover:bg-gray-100 hover:text-indigo-600"
              }`}
              title={
                isAiSection
                  ? "این سکشن توسط هوش مصنوعی تولید می‌شود و مدیریت سوالات دستی ندارد."
                  : t("exam_builder.manage_items")
              }
              aria-label={t("exam_builder.manage_items")}
            >
              <Settings2 size={18} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <ListChecks className="w-4 h-4" />
            {isAiSection ? (aiCount ?? 0) : itemsCount} {t("exam_builder.items")}
          </span>

          {time !== null && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-4 h-4" /> {time} {t("exam_builder.minutes")}
            </span>
          )}

          {isListeningSection && !isAiSection && (
            <span className="inline-flex items-center gap-1 text-blue-600">
              <Headphones className="w-4 h-4" /> {t("exam_builder.listening_on")}
            </span>
          )}

          {section?.displayMode === "all_at_once" && !isAiSection && (
            <span className="inline-flex items-center gap-1 text-purple-600">
              <Layout className="w-4 h-4" /> {t("exam_builder.all_at_once_on")}
            </span>
          )}

          {section?.shuffleItems && !isAiSection && (
            <span className="inline-flex items-center gap-1 text-teal-600">
              {t("exam_builder.shuffle_on")}
            </span>
          )}
        </div>
      </div>

      <ConfirmModal
        open={deleteOpen}
        title={t("exam_builder.delete_section_title", "حذف بخش")}
        message={t(
          "exam_builder.delete_section_confirm",
          "آیا از حذف این بخش مطمئن هستید؟ (سؤالات داخل این بخش پاک نمی‌شوند، فقط از این آزمون جدا می‌شوند.)"
        )}
        confirmText={t("common.delete", "حذف")}
        cancelText={t("common.cancel", "انصراف")}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
}

SectionCard.propTypes = {
  section: PropTypes.shape({
    type: PropTypes.string, // ✅ اضافه
    ai: PropTypes.shape({
      mode: PropTypes.string,
      count: PropTypes.number,
      difficulty: PropTypes.string,
      category: PropTypes.any,
      language: PropTypes.string,
    }),
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    title_fa: PropTypes.string,
    title_en: PropTypes.string,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    description_fa: PropTypes.string,
    description_en: PropTypes.string,
    timeLimitSec: PropTypes.number,
    shuffleItems: PropTypes.bool,
    items: PropTypes.array,
    audioFile: PropTypes.shape({ url: PropTypes.string }),
    displayMode: PropTypes.string,
    kind: PropTypes.string,
  }).isRequired,
  index: PropTypes.number,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onManageItems: PropTypes.func,
};
